import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Autocomplete,
  Checkbox,
  Avatar,
  IconButton,
  Stack,
} from '@mui/material';
import { People, Preview, CheckBox, CheckBoxOutlineBlank, FilterAlt, Close as CloseIcon, Add } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import AudienceReviewDialog, { ReviewMember } from '../audience/AudienceReviewDialog';

export const SEGMENTS = [
  // Studio-scoped (only available to studio owners — always filtered to their users)
  { value: 'ALL_STUDIO_USERS', label: 'All My Users', description: 'Everyone who has booked with your studio', icon: '🏋️', studioOnly: true },
  // Admin + Studio
  { value: 'ALL_USERS', label: 'All Users', description: 'Every user with a registered device', icon: '👥' },
  { value: 'SPECIFIC_USERS', label: 'Specific Users', description: 'Hand-pick individual users', icon: '🎯' },
  { value: 'CITY', label: 'By City', description: 'Users in a specific city', icon: '🏙️' },
  { value: 'ABANDONED_CHECKOUT', label: 'Abandoned Checkout', description: 'Reached checkout but never paid', icon: '🛒' },
  { value: 'INACTIVE_USERS', label: 'Inactive Users', description: 'No bookings in last N days', icon: '😴' },
  { value: 'UPCOMING_CLASS_TODAY', label: "Today's Class Attendees", description: 'Users with a session booked today', icon: '💪' },
  { value: 'UPCOMING_EVENT_TODAY', label: "Today's Event Attendees", description: 'Users with an event booked today', icon: '🎪' },
  { value: 'GENDER', label: 'By Gender', description: 'Target a specific gender', icon: '⚧' },
  { value: 'NEW_USERS', label: 'New Users', description: 'Users who joined recently', icon: '🆕' },
  // Pack-based
  { value: 'PACK_EXPIRING', label: 'Pack Expiring Soon', description: 'Active pack expires within N days', icon: '⏳' },
  { value: 'PACK_NOT_STARTED', label: 'Pack Not Used', description: 'Bought a pack but never booked a class', icon: '📦' },
  { value: 'PACK_LOW_BALANCE', label: 'Low Pack Balance', description: 'Only N classes left in their pack', icon: '🪫' },
  // Lifecycle
  { value: 'NEVER_BOOKED', label: 'Never Booked', description: 'Registered but zero completed bookings', icon: '🌱' },
];

// Segments available to studio owners (always scoped to their own users by the backend)
export const STUDIO_SEGMENTS = ['ALL_STUDIO_USERS', 'SPECIFIC_USERS', 'INACTIVE_USERS', 'UPCOMING_CLASS_TODAY', 'PACK_EXPIRING'];

interface SegmentConfig {
  userIds?: string[];
  city?: string;
  gender?: string;
  daysBack?: number;
  daysNew?: number;
  daysUntilExpiry?: number;
  maxClassesLeft?: number;
  // Cross-segment narrowing filters
  filterCity?: string;
  filterGender?: string;
  /** Unticked in "Review recipients" — never sent to. */
  excludeUserIds?: string[];
}

interface SegmentSelectorProps {
  segment: string;
  segmentConfig: SegmentConfig;
  onSegmentChange: (segment: string) => void;
  onConfigChange: (config: SegmentConfig) => void;
  allowedSegments?: string[]; // If set, only show these segments
  previewFn?: (segment: string, config: any, includeMembers?: boolean) => Promise<any>; // Override the preview API call
}

interface PreviewResult {
  count: number;
  matched?: number;
  sample: { id: string; name: string; email: string; city: string; hasToken: boolean }[];
}

const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : parts[0][0].toUpperCase();
};

const SegmentSelector: React.FC<SegmentSelectorProps> = ({
  segment,
  segmentConfig,
  onSegmentChange,
  onConfigChange,
  allowedSegments,
  previewFn,
}) => {
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewMembers, setReviewMembers] = useState<ReviewMember[] | null>(null);
  const [reviewTruncated, setReviewTruncated] = useState(false);
  const excludedCount = segmentConfig.excludeUserIds?.length ?? 0;

  // User search state
  const [searchText, setSearchText] = useState('');       // controlled input value
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleSegments = allowedSegments
    ? SEGMENTS.filter((s) => allowedSegments.includes(s.value))
    : SEGMENTS.filter((s) => !s.studioOnly); // hide studio-only segments from admin

  const runPreview = (config: SegmentConfig, includeMembers = false): Promise<any> =>
    previewFn
      ? previewFn(segment, config, includeMembers)
      : apiService.getSegmentPreview(segment, config, includeMembers);

  const handlePreview = async (config: SegmentConfig = segmentConfig) => {
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      setPreview(await runPreview(config));
    } catch (err: any) {
      setPreviewError(err.message || 'Failed to preview segment');
    } finally {
      setPreviewLoading(false);
    }
  };

  // "Review recipients": everyone matched, tick/untick who gets it.
  const openReview = async () => {
    setReviewOpen(true);
    setReviewMembers(null);
    try {
      const result: any = await runPreview(segmentConfig, true);
      setReviewTruncated(!!result?.membersTruncated);
      setReviewMembers(
        (result?.members ?? []).map((m: any) => ({
          id: m.id,
          name: m.name || 'Unknown',
          subtitle: [m.phone, m.email].filter(Boolean).join(' · '),
          detail: m.city,
          badge: m.hasToken ? undefined : 'No app',
        })),
      );
    } catch (err: any) {
      setReviewOpen(false);
      setPreviewError(err.message || 'Could not load recipients');
    }
  };

  const applyExclusions = (excludeUserIds: string[]) => {
    const next = { ...segmentConfig, excludeUserIds };
    onConfigChange(next);
    setReviewOpen(false);
    handlePreview(next);
  };

  // Controlled input: preserve text even after selection, debounce API call by 1s
  const handleInputChange = useCallback((_: any, val: string, reason: string) => {
    // 'reset' fires after user selects — keep the typed text as-is
    if (reason === 'reset') return;

    setSearchText(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!val.trim()) {
      setSearchResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const res: any = await apiService.searchUsers(val.trim());
        setSearchResults(res.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 1000); // 1 second debounce
  }, []);

  const handleUserSelect = (_: any, selected: any[]) => {
    setSelectedUsers(selected);
    onConfigChange({ ...segmentConfig, userIds: selected.map((u) => u._id) });
  };

  // "Browse all": the complete reachable list, tick who should get it.
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseAll, setBrowseAll] = useState<ReviewMember[] | null>(null);
  const [browseRaw, setBrowseRaw] = useState<any[]>([]);
  const allSegment = allowedSegments?.includes('ALL_STUDIO_USERS') ? 'ALL_STUDIO_USERS' : 'ALL_USERS';

  const openBrowse = async () => {
    setBrowseOpen(true);
    setBrowseAll(null);
    try {
      const result: any = previewFn
        ? await previewFn(allSegment, {}, true)
        : await apiService.getSegmentPreview(allSegment, {}, true);
      const members = result?.members ?? [];
      setBrowseRaw(members);
      setBrowseAll(
        members.map((m: any) => ({
          id: m.id,
          name: m.name,
          subtitle: [m.phone, m.email].filter(Boolean).join(' · '),
          detail: m.city,
          badge: m.hasToken ? undefined : 'No app',
        })),
      );
    } catch (err: any) {
      setBrowseOpen(false);
      setPreviewError(err.message || 'Could not load users');
    }
  };

  const applyBrowse = (excludedIds: string[]) => {
    const excluded = new Set(excludedIds);
    const picked = browseRaw
      .filter((m) => !excluded.has(m.id))
      .map((m) => ({ _id: m.id, name: m.name, email: m.email, phoneNumber: m.phone, city: m.city }));
    setSelectedUsers(picked);
    onConfigChange({ ...segmentConfig, userIds: picked.map((u) => u._id) });
    setBrowseOpen(false);
  };

  const handleClearAll = () => {
    setSelectedUsers([]);
    setSearchText('');
    setSearchResults([]);
    onConfigChange({ ...segmentConfig, userIds: [] });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Segment dropdown */}
      <FormControl fullWidth>
        <InputLabel>Target Audience</InputLabel>
        <Select
          value={segment}
          label="Target Audience"
          onChange={(e) => {
            onSegmentChange(e.target.value);
            // Preserve cross-segment filters; clear segment-specific params
            const { filterCity, filterGender } = segmentConfig;
            onConfigChange({ ...(filterCity ? { filterCity } : {}), ...(filterGender ? { filterGender } : {}) });
            setPreview(null);
            setSearchText('');
            setSearchResults([]);
            setSelectedUsers([]);
          }}
        >
          {visibleSegments.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontSize: '1.2rem' }}>{s.icon}</Typography>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{s.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* SPECIFIC_USERS — server-side search with 1s debounce */}
      {segment === 'SPECIFIC_USERS' && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Search below, or pick from everyone:
            </Typography>
            <Button size="small" variant="outlined" startIcon={<People />} onClick={openBrowse} sx={{ borderRadius: 2, fontWeight: 700 }}>
              Browse all users
            </Button>
          </Stack>
          <AudienceReviewDialog
            open={browseOpen}
            onClose={() => setBrowseOpen(false)}
            onApply={applyBrowse}
            members={browseAll}
            initiallyExcluded={(browseAll ?? []).map((m) => m.id).filter((id) => !(segmentConfig.userIds ?? []).includes(id))}
            title="Pick who gets this notification"
            applyLabel={(n) => `Select ${n} ${n === 1 ? 'user' : 'users'}`}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={searchResults}
            loading={searchLoading}
            // Controlled input — prevents text from being wiped on selection
            inputValue={searchText}
            onInputChange={handleInputChange}
            // Bypass MUI's own filter — we get pre-filtered results from backend
            filterOptions={(x) => x}
            getOptionLabel={(u) => u.name || u.email || u.phoneNumber || 'Unknown'}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            value={selectedUsers}
            onChange={handleUserSelect}
            ListboxProps={{ style: { maxHeight: 300 } }}
            noOptionsText={
              searchLoading
                ? 'Searching...'
                : searchText.trim().length === 0
                ? '🔍 Type to search users...'
                : 'No users found'
            }
            renderOption={(props, option, { selected }) => {
              const { key, ...restProps } = props as any;
              const nameParts = (option.name || '').trim().split(' ');
              const firstName = nameParts[0] || '';
              const lastName = nameParts.slice(1).join(' ') || '';
              return (
                <Box component="li" key={key} {...restProps} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 2 }}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank sx={{ fontSize: 20 }} />}
                    checkedIcon={<CheckBox sx={{ fontSize: 20 }} />}
                    checked={selected}
                    sx={{ p: 0 }}
                  />
                  <Avatar
                    src={option.image || undefined}
                    sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: '#2196f3', flexShrink: 0 }}
                  >
                    {getInitials(option.name || '')}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{firstName}</Typography>
                      {lastName && <Typography variant="body2" color="text.secondary" noWrap>{lastName}</Typography>}
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.email || option.phoneNumber || 'No contact'}
                      {option.city ? ` · ${option.city}` : ''}
                    </Typography>
                  </Box>
                  {option.fcmToken && (
                    <Chip label="Active" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(76,175,80,0.1)', color: '#2e7d32' }} />
                  )}
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((u, index) => {
                const firstName = (u.name || '').trim().split(' ')[0] || u.email || 'User';
                const { key, ...tagProps } = getTagProps({ index }) as any;
                return (
                  <Chip
                    key={u._id}
                    avatar={
                      <Avatar src={u.image || undefined} sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                        {getInitials(u.name || '')}
                      </Avatar>
                    }
                    label={firstName}
                    size="small"
                    sx={{ fontWeight: 600 }}
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Users"
                placeholder="Type name, email, phone or city..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Selected summary bar */}
          {selectedUsers.length > 0 && (
            <Box sx={{ mt: 1, px: 2, py: 1, bgcolor: 'rgba(33,150,243,0.06)', border: '1px solid rgba(33,150,243,0.2)', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People sx={{ fontSize: 16, color: '#2196f3' }} />
                <Typography variant="caption" fontWeight={600} color="primary">
                  {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                </Typography>
              </Box>
              <Button size="small" variant="text" onClick={handleClearAll} sx={{ fontSize: '0.7rem', py: 0, color: 'text.secondary' }}>
                Clear all
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* CITY */}
      {segment === 'CITY' && (
        <TextField
          label="City"
          placeholder="e.g. Mumbai"
          value={segmentConfig.city || ''}
          onChange={(e) => onConfigChange({ ...segmentConfig, city: e.target.value })}
          fullWidth
        />
      )}

      {/* GENDER */}
      {segment === 'GENDER' && (
        <FormControl fullWidth>
          <InputLabel>Gender</InputLabel>
          <Select
            value={segmentConfig.gender || ''}
            label="Gender"
            onChange={(e) => onConfigChange({ ...segmentConfig, gender: e.target.value })}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* ABANDONED_CHECKOUT / INACTIVE_USERS */}
      {(segment === 'ABANDONED_CHECKOUT' || segment === 'INACTIVE_USERS') && (
        <TextField
          label="Lookback Period (days)"
          type="number"
          value={segmentConfig.daysBack || 7}
          onChange={(e) => onConfigChange({ ...segmentConfig, daysBack: parseInt(e.target.value) || 7 })}
          helperText={
            segment === 'ABANDONED_CHECKOUT'
              ? 'Target users who abandoned checkout within this many days'
              : 'Target users with no bookings in this many days'
          }
          inputProps={{ min: 1, max: 365 }}
          fullWidth
        />
      )}

      {/* NEW_USERS */}
      {segment === 'NEW_USERS' && (
        <TextField
          label="Joined within (days)"
          type="number"
          value={segmentConfig.daysNew || 7}
          onChange={(e) => onConfigChange({ ...segmentConfig, daysNew: parseInt(e.target.value) || 7 })}
          helperText="Target users who registered within this many days"
          inputProps={{ min: 1, max: 90 }}
          fullWidth
        />
      )}

      {/* PACK_EXPIRING */}
      {segment === 'PACK_EXPIRING' && (
        <TextField
          label="Expiring within (days)"
          type="number"
          value={segmentConfig.daysUntilExpiry || 7}
          onChange={(e) => onConfigChange({ ...segmentConfig, daysUntilExpiry: parseInt(e.target.value) || 7 })}
          helperText="Target users whose active pack expires within this many days (and still have classes left)"
          inputProps={{ min: 1, max: 30 }}
          fullWidth
        />
      )}

      {/* PACK_LOW_BALANCE */}
      {segment === 'PACK_LOW_BALANCE' && (
        <TextField
          label="Classes remaining (max)"
          type="number"
          value={segmentConfig.maxClassesLeft || 2}
          onChange={(e) => onConfigChange({ ...segmentConfig, maxClassesLeft: parseInt(e.target.value) || 2 })}
          helperText="Target users with this many or fewer classes left in their active pack"
          inputProps={{ min: 1, max: 10 }}
          fullWidth
        />
      )}

      {/* ─── Additional Filters ─────────────────────── */}
      <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: segmentConfig.filterCity || segmentConfig.filterGender ? 2 : 0 }}>
          <FilterAlt sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Narrow down further
          </Typography>
          <Box sx={{ flex: 1 }} />
          {/* Add city filter button — hidden when CITY segment (redundant) or already active */}
          {segment !== 'CITY' && !segmentConfig.filterCity && (
            <Chip
              icon={<Add sx={{ fontSize: 14 }} />}
              label="City"
              size="small"
              variant="outlined"
              clickable
              onClick={() => onConfigChange({ ...segmentConfig, filterCity: '' })}
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          {/* Add gender filter button — hidden when GENDER segment or already active */}
          {segment !== 'GENDER' && !segmentConfig.filterGender && (
            <Chip
              icon={<Add sx={{ fontSize: 14 }} />}
              label="Gender"
              size="small"
              variant="outlined"
              clickable
              onClick={() => onConfigChange({ ...segmentConfig, filterGender: '' })}
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {/* Active filters */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {segmentConfig.filterCity !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="City filter"
                placeholder="e.g. Bengaluru"
                value={segmentConfig.filterCity || ''}
                onChange={(e) => onConfigChange({ ...segmentConfig, filterCity: e.target.value })}
                size="small"
                fullWidth
                helperText="Only include users in this city"
              />
              <IconButton
                size="small"
                onClick={() => {
                  const c = { ...segmentConfig };
                  delete c.filterCity;
                  onConfigChange(c);
                  setPreview(null);
                }}
                sx={{ mt: -2.5, flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
          {segmentConfig.filterGender !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Gender filter</InputLabel>
                <Select
                  value={segmentConfig.filterGender || ''}
                  label="Gender filter"
                  onChange={(e) => onConfigChange({ ...segmentConfig, filterGender: e.target.value })}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => {
                  const c = { ...segmentConfig };
                  delete c.filterGender;
                  onConfigChange(c);
                  setPreview(null);
                }}
                sx={{ flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
          {!segmentConfig.filterCity && !segmentConfig.filterGender && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No extra filters applied. Use the chips above to narrow your audience.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Preview button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={previewLoading ? <CircularProgress size={16} /> : <Preview />}
          onClick={() => handlePreview()}
          disabled={previewLoading}
          sx={{ borderRadius: 2 }}
        >
          Preview Audience
        </Button>
        {preview && (
          <Chip
            icon={<People />}
            label={`${preview.count} user${preview.count !== 1 ? 's' : ''} will receive this`}
            color={preview.count > 0 ? 'primary' : 'default'}
            variant="filled"
          />
        )}
        {preview && ((preview.matched ?? preview.count) > 0) && (
          <Button size="small" startIcon={<People />} onClick={openReview} sx={{ fontWeight: 700 }}>
            Review recipients
          </Button>
        )}
        {excludedCount > 0 && (
          <Chip
            size="small"
            color="warning"
            label={`${excludedCount} left out`}
            onDelete={() => applyExclusions([])}
          />
        )}
      </Box>

      <AudienceReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onApply={applyExclusions}
        members={reviewMembers}
        truncated={reviewTruncated}
        initiallyExcluded={segmentConfig.excludeUserIds ?? []}
        title="Who gets this notification?"
      />

      {previewError && <Alert severity="error" sx={{ borderRadius: 2 }}>{previewError}</Alert>}

      {preview && preview.count > 0 && (
        <Box sx={{ p: 1.5, bgcolor: 'rgba(33,150,243,0.04)', borderRadius: 2, border: '1px solid rgba(33,150,243,0.15)' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
            Sample recipients:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {preview.sample.map((u) => (
              <Chip
                key={u.id}
                avatar={<Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: '#2196f3' }}>{(u.name || '?')[0].toUpperCase()}</Avatar>}
                label={(u.name || '').trim().split(' ')[0] || u.email || 'User'}
                size="small"
                variant="outlined"
                color={u.hasToken ? 'success' : 'default'}
              />
            ))}
            {preview.count > 5 && (
              <Chip label={`+${preview.count - 5} more`} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
      )}

      {preview && preview.count === 0 && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No users found matching this segment. Try adjusting the filters.
        </Alert>
      )}
    </Box>
  );
};

export default SegmentSelector;
